/**
 * The MobX store owning the live FlexLayout `Model` (ADR-UI-003 §5). The
 * ONLY place that touches `flexlayout-react`'s `Model`/`Actions` directly;
 * every mutation goes through this store's methods, never a raw
 * `model.doAction` call from elsewhere.
 *
 * Named `WorkspaceModelStore` (not `WorkspaceStore`) so it coexists with the
 * OLD `../workspace-store.ts`'s `WorkspaceStore` class, which
 * `operations-route.tsx` still uses until Round B's cutover.
 */
import { Actions, DockLocation, Model, TabNode, TabSetNode } from "flexlayout-react";
import type { IJsonModel, IJsonTabNode } from "flexlayout-react";
import { makeAutoObservable, observable, runInAction } from "mobx";
import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import { applyPermissionFilter, applyPerspectiveRelevanceFilter, applyRegistryWhitelist, CURRENT_SCHEMA_VERSION, restoreWorkspaceLayout } from "./layout-envelope";
import { InMemoryWorkspaceEnvelopeStore } from "./envelope-store";
import type { WorkspaceEnvelopeStore } from "./envelope-store";
import type { WorkspaceComponentRegistry } from "../registry/component-registry";
import type { DeviceMode, WorkspaceComponentId, WorkspaceLayoutEnvelope } from "../types";

const SAVE_DEBOUNCE_MS = 500;

export interface WorkspaceOpenRequest {
  readonly component: WorkspaceComponentId;
  readonly config?: unknown;
  readonly matchConfig?: Readonly<Record<string, unknown>> | undefined;
  /** FlexLayout tabset or border id, for example `workspace-center` or `border_left`. */
  readonly targetNodeId?: string | undefined;
}

export interface WorkspaceFocusRequest {
  readonly component: WorkspaceComponentId;
  readonly matchConfig?: Readonly<Record<string, unknown>> | undefined;
}

export interface WorkspacePrimaryFocusRequest extends WorkspaceOpenRequest {
  /** Canonical C1 tabset. Falls back to the first tabset if a stale layout lost it. */
  readonly targetNodeId: string;
}

export interface WorkspacePresetRecord {
  readonly id: string;
  readonly name: string;
  readonly envelope: WorkspaceLayoutEnvelope;
}

export interface WorkspaceModelStoreOptions {
  readonly registry: WorkspaceComponentRegistry;
  readonly canDiscover: (required: readonly PermissionCode[]) => boolean;
  /** Role/device-mode default envelope, re-derived on every reset (a supplier, not a fixed value). */
  readonly defaultEnvelope: () => WorkspaceLayoutEnvelope;
  /** Role/device relevance applied to presets, restores, catalogue entries and explicit opens. */
  readonly isComponentRelevant?: (component: WorkspaceComponentId) => boolean;
  /** Optional profile-specific normalization applied after safe persisted-layout restoration. */
  readonly restoreTransform?: (envelope: WorkspaceLayoutEnvelope) => WorkspaceLayoutEnvelope;
  readonly envelopeStore?: WorkspaceEnvelopeStore | undefined;
  readonly storageKey?: string | undefined;
  /** Older identity keys tried only when the current key has no value. */
  readonly legacyStorageKeys?: readonly string[] | undefined;
  readonly saveDebounceMs?: number | undefined;
}

function configMatches(actual: unknown, expected: Readonly<Record<string, unknown>>): boolean {
  if (typeof actual !== "object" || actual === null) return false;
  const record = actual as Record<string, unknown>;
  return Object.entries(expected).every(([key, value]) => record[key] === value);
}

export class WorkspaceModelStore {
  model: Model;
  presetId: string;
  deviceMode: DeviceMode;
  presetRecords: WorkspacePresetRecord[] = [];

  private readonly registry: WorkspaceComponentRegistry;
  private readonly canDiscover: (required: readonly PermissionCode[]) => boolean;
  private readonly defaultEnvelopeSupplier: () => WorkspaceLayoutEnvelope;
  private readonly isComponentRelevant: ((component: WorkspaceComponentId) => boolean) | undefined;
  private readonly restoreTransform:
    | ((envelope: WorkspaceLayoutEnvelope) => WorkspaceLayoutEnvelope)
    | undefined;
  private readonly envelopeStore: WorkspaceEnvelopeStore;
  private readonly storageKey: string;
  private readonly legacyStorageKeys: readonly string[];
  private readonly saveDebounceMs: number;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: WorkspaceModelStoreOptions) {
    this.registry = options.registry;
    this.canDiscover = options.canDiscover;
    this.defaultEnvelopeSupplier = options.defaultEnvelope;
    this.isComponentRelevant = options.isComponentRelevant;
    this.restoreTransform = options.restoreTransform;
    this.envelopeStore = options.envelopeStore ?? new InMemoryWorkspaceEnvelopeStore();
    this.storageKey = options.storageKey ?? "default";
    this.legacyStorageKeys = options.legacyStorageKeys ?? [];
    this.saveDebounceMs = options.saveDebounceMs ?? SAVE_DEBOUNCE_MS;

    const initial = this.defaultEnvelopeSupplier();
    this.presetId = initial.presetId;
    this.deviceMode = initial.deviceMode;
    this.model = Model.fromJson(initial.model as IJsonModel);

    makeAutoObservable(this, { model: observable.ref }, { autoBind: true });
    this.attachChangeListener();
  }

  /** Reads any persisted envelope, migrating/whitelisting/permission-filtering it or falling back to the role default. */
  async hydrate(): Promise<void> {
    let raw = await this.envelopeStore.load(this.storageKey);
    let migratedFromLegacyKey = false;
    if (raw === null) {
      for (const legacyKey of this.legacyStorageKeys) {
        raw = await this.envelopeStore.load(legacyKey);
        if (raw !== null) {
          migratedFromLegacyKey = true;
          break;
        }
      }
    }
    const fallback = this.defaultEnvelopeSupplier();
    const restored = restoreWorkspaceLayout(
      raw,
      fallback,
      this.registry,
      this.canDiscover,
      this.isComponentRelevant,
    );
    const transformed = this.restoreTransform?.(restored) ?? restored;
    runInAction(() => {
      this.applyEnvelope(transformed);
    });
    if (migratedFromLegacyKey) {
      // Copy, do not delete: another role/perspective may still need to read
      // the former shared key during its own first migration.
      await this.envelopeStore.save(this.storageKey, this.snapshot());
    }
  }

  open(request: WorkspaceOpenRequest): void {
    const definition = this.registry.get(request.component);
    if (definition === undefined) return;
    if (!this.canDiscover(definition.requiredPermissions)) return;
    if (!(this.isComponentRelevant?.(request.component) ?? true)) return;

    if (definition.singleton === true && this.findTab(request.component, request.matchConfig) !== undefined) {
      this.focusExisting({ component: request.component, matchConfig: request.matchConfig });
      return;
    }

    const requestedTarget =
      request.targetNodeId !== undefined &&
      this.model.getNodeById(request.targetNodeId) !== undefined
        ? request.targetNodeId
        : undefined;
    const targetId =
      requestedTarget ??
      this.model.getActiveTabset()?.getId() ??
      this.model.getFirstTabSet()?.getId();
    if (targetId === undefined) return;

    const json: IJsonTabNode = {
      type: "tab",
      component: request.component,
      name: definition.titleKey,
      config: request.config,
    };
    this.model.doAction(Actions.addTab(json, targetId, DockLocation.CENTER, -1, true));
  }

  focusExisting(request: WorkspaceFocusRequest): boolean {
    const tab = this.findTab(request.component, request.matchConfig);
    if (tab === undefined) return false;
    const parent = tab.getParent();
    if (parent instanceof TabSetNode) {
      const layoutId = parent.getLayoutId();
      const maximized = this.model.getMaximizedTabset(layoutId);
      if (maximized !== undefined && maximized.getId() !== parent.getId()) {
        // A saved maximized tabset hides every sibling. Restore the complete
        // layout before focusing a deep-linked panel in another tabset.
        this.model.doAction(Actions.maximizeToggle(maximized.getId(), layoutId));
      }
      this.model.doAction(Actions.selectTab(tab.getId()));
      // `selectTab` only selects within the tab's own tabset. Explicitly
      // activate the parent so keyboard focus and future opens follow it.
      this.model.doAction(Actions.setActiveTabset(parent.getId(), layoutId));
    } else {
      this.model.doAction(Actions.selectTab(tab.getId()));
    }
    return true;
  }

  /**
   * Focuses a header/deep-link target as the first pinned C1 tab. This changes
   * presentation only; registry relevance and discovery permissions are
   * checked before the model is touched, and panel actions still authorize
   * independently.
   */
  focusPrimary(request: WorkspacePrimaryFocusRequest): void {
    const definition = this.registry.get(request.component);
    if (definition === undefined) return;
    if (!this.canDiscover(definition.requiredPermissions)) return;
    if (!(this.isComponentRelevant?.(request.component) ?? true)) return;

    const requestedTarget = this.model.getNodeById(request.targetNodeId);
    const target = requestedTarget instanceof TabSetNode
      ? requestedTarget
      : this.model.getFirstTabSet();
    if (target === undefined) return;

    let tab = this.findTab(request.component, request.matchConfig);
    if (tab === undefined) {
      const json: IJsonTabNode = {
        type: "tab",
        component: request.component,
        name: definition.titleKey,
        config: request.config,
      };
      this.model.doAction(Actions.addTab(
        json,
        target.getId(),
        DockLocation.CENTER,
        0,
        true,
      ));
      tab = this.findTab(request.component, request.matchConfig);
    } else {
      this.model.doAction(Actions.moveNode(
        tab.getId(),
        target.getId(),
        DockLocation.CENTER,
        0,
        true,
      ));
      tab = this.findTab(request.component, request.matchConfig);
    }
    if (tab === undefined) return;
    this.model.doAction(Actions.setTabPinned(tab.getId(), true));
    this.model.doAction(Actions.updateNodeAttributes(tab.getId(), { enableClose: false }));
    this.focusExisting({ component: request.component, matchConfig: request.matchConfig });
  }

  close(tabId: string): void {
    this.model.doAction(Actions.deleteTab(tabId));
  }

  closeByContext(contextMatch: Readonly<Record<string, unknown>>): void {
    const ids: string[] = [];
    this.model.visitNodes((node) => {
      if (!(node instanceof TabNode)) return;
      if (configMatches(node.getConfig(), contextMatch)) ids.push(node.getId());
    });
    for (const id of ids) this.close(id);
  }

  resetToDefault(): void {
    this.applyEnvelope(this.defaultEnvelopeSupplier());
    // Clearing only the current key would let the next hydrate import an old
    // migration source again, undoing the operator's explicit reset. Retire
    // every fallback for this scoped workspace at the same time.
    for (const key of new Set([this.storageKey, ...this.legacyStorageKeys])) {
      void this.envelopeStore.clear(key);
    }
  }

  /** Read-only catalogue state; all mutations still flow through `open`. */
  isOpen(component: WorkspaceComponentId): boolean {
    return this.findTab(component, undefined) !== undefined;
  }

  applyPreset(envelope: WorkspaceLayoutEnvelope): void {
    const model = envelope.model as IJsonModel;
    const whitelisted = applyRegistryWhitelist(model, this.registry);
    const permitted = applyPermissionFilter(whitelisted, this.registry, this.canDiscover);
    const relevant = this.isComponentRelevant === undefined
      ? permitted
      : applyPerspectiveRelevanceFilter(permitted, this.isComponentRelevant);
    this.applyEnvelope({ ...envelope, model: relevant });
  }

  saveAsPreset(name: string): WorkspacePresetRecord {
    const record: WorkspacePresetRecord = {
      id: globalThis.crypto.randomUUID(),
      name,
      envelope: this.snapshot(),
    };
    this.presetRecords = [...this.presetRecords, record];
    return record;
  }

  renamePreset(id: string, name: string): void {
    this.presetRecords = this.presetRecords.map((record) => (record.id === id ? { ...record, name } : record));
  }

  deletePreset(id: string): void {
    this.presetRecords = this.presetRecords.filter((record) => record.id !== id);
  }

  /** Cancels any pending debounced save and persists immediately. */
  async saveNow(): Promise<void> {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    await this.persistNow();
  }

  snapshot(): WorkspaceLayoutEnvelope {
    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      presetId: this.presetId,
      deviceMode: this.deviceMode,
      model: this.model.toJson(),
    };
  }

  private findTab(
    component: WorkspaceComponentId,
    matchConfig: Readonly<Record<string, unknown>> | undefined,
  ): TabNode | undefined {
    let found: TabNode | undefined;
    this.model.visitNodes((node) => {
      if (found !== undefined) return;
      if (!(node instanceof TabNode)) return;
      if (node.getComponent() !== component) return;
      if (matchConfig !== undefined && !configMatches(node.getConfig(), matchConfig)) return;
      found = node;
    });
    return found;
  }

  private applyEnvelope(envelope: WorkspaceLayoutEnvelope): void {
    this.presetId = envelope.presetId;
    this.deviceMode = envelope.deviceMode;
    this.model = Model.fromJson(envelope.model as IJsonModel);
    this.attachChangeListener();
  }

  private attachChangeListener(): void {
    this.model.addChangeListener(this.handleModelChange);
  }

  private readonly handleModelChange = (): void => {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.persistNow();
    }, this.saveDebounceMs);
  };

  private async persistNow(): Promise<void> {
    await this.envelopeStore.save(this.storageKey, this.snapshot());
  }
}
